import React, {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { FcDownload } from "react-icons/fc";
import { makeStyles } from "@material-ui/core/styles";
import { FcSearch } from "react-icons/fc";
import { AiFillDelete } from "react-icons/ai";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment/moment";
import {handleDownloadExcel} from "../ExportToExcel"

const useStyles = makeStyles({
  card: {
    maxWidth: "1100px",
    margin: "0 auto",
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },
  dateInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e2e8f0",
  },
  dateRange: {
    color: "#1f2937",
    fontWeight: 600,
  },
  tableHeadRow: {
    "& th": {
      backgroundColor: "#f4f6fb",
      color: "#1f2937",
      fontWeight: 700,
      borderBottom: "1px solid #e2e8f0",
    },
  },
  tableRow: {
    "&:nth-child(even)": {
      backgroundColor: "#fafbfd",
    },
    "&:hover": {
      backgroundColor: "#e8f1fb",
    },
    "&:last-child td": {
      border: 0,
    },
  },
  iconBtn: {
    background: "none",
    border: "none",
    borderRadius: "50%",
    padding: 6,
    cursor: "pointer",
    display: "inline-flex",
    verticalAlign: "middle",
    transition: "background-color 0.15s ease",
    "&:hover:not(:disabled)": {
      backgroundColor: "#e8f1fb",
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "24px",
    color: "#607d8b",
  },
});

const ReportResult = ({
  startDate,
  endDate,
  reports,
  setReports,
  userid,
  sid,
  searched,
}) => {
  const classes = useStyles();
  const [deletingId, setDeletingId] = useState(null);

  const [exportData, setExportData] = useState({
    headers: [
      "Class", "Subject", "Lesson Name", "Date"
    ],
    body: []
  }
  )

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (!result.isConfirmed) return;

    setDeletingId(id);
    try {
      const response = await axios.post(
        "https://topschool.prisms.in/rest/index.php/staff_list.json",
        {
          fun_name: "deleteWeeklyLessonPlan",
          staff_id: userid,
          sid,
          id,
        }
      );
      if (response.data && response.data.success !== false) {
        setReports(reports.filter((report) => report.lesson_id !== id));
        Swal.fire("Deleted!", "Your file has been deleted.", "success");
      } else {
        Swal.fire("Error", (response.data && response.data.message) || "Could not delete the report", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Could not delete the report. Please try again.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const myArray = (reports || []).map(item => {
      return {
        div_name: item.div_name,
        subject: item.subject,
        lession_name: item.lession_name,
        date: item.month
      }
    });
    setExportData((prev) => ({...prev, body: myArray}))
  }, [reports])

  return (
    <div className={classes.card}>
      <div className={classes.dateInfo}>
        <span className={classes.dateRange}>
          {startDate} - {endDate}
        </span>
        <button
          type="button"
          className={classes.iconBtn}
          aria-label="Download report as Excel"
          disabled={!reports || reports.length === 0}
          onClick={()=>handleDownloadExcel(exportData.headers, exportData.body)}
        >
          <FcDownload size={24} />
        </button>
      </div>
      <TableContainer>
        <Table aria-label="weekly reports table">
          <TableHead>
            <TableRow className={classes.tableHeadRow}>
              <TableCell align="center">Class</TableCell>
              <TableCell align="center">Subject</TableCell>
              <TableCell>Lesson Name</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!reports || reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.emptyState}>
                  {searched ? "No weekly reports found for this date range." : "Choose a date range and click Submit to see reports."}
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.lesson_id} className={classes.tableRow}>
                  <TableCell align="center">{report.div_name}</TableCell>
                  <TableCell align="center">{report.subject}</TableCell>
                  <TableCell component="th" scope="row">
                    {report.lession_name}
                  </TableCell>
                  <TableCell align="center">
                    {report.month ? moment(report.month).format("DD-MM-YYYY") : "-"}
                  </TableCell>

                  <TableCell align="center">
                    <Link
                      to={`/reportWeeklyView/${sid}/${userid}/${report.lesson_id}`}
                      aria-label={`View weekly plan for ${report.subject}`}
                      className={classes.iconBtn}
                    >
                      <FcSearch size={24} />
                    </Link>

                    <button
                      type="button"
                      className={classes.iconBtn}
                      style={{ marginLeft: 8 }}
                      aria-label={`Delete weekly plan for ${report.subject}`}
                      disabled={deletingId === report.lesson_id}
                      onClick={() => handleDelete(report.lesson_id)}
                    >
                      <AiFillDelete
                        size={24}
                        color={deletingId === report.lesson_id ? "grey" : "#d32f2f"}
                      />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ReportResult;
